import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';

import type {
	useContributorsDataQuery$data,
	useContributorsDataQuery$variables,
} from './__generated__/useContributorsDataQuery.graphql.ts';
import type { ParsedMacroParams } from './useParsedMacroParams.ts';

interface UseContributorsDataOptions {
	contentId: string;
	skip?: boolean;
	macroParams?: ParsedMacroParams['macroParamsForQuery'];
}

interface UseContributorsDataResult {
	data: useContributorsDataQuery$data | undefined;
	loading: boolean;
	error: Error | null;
}

export const useContributorsData = ({
	contentId,
	skip,
	macroParams,
}: UseContributorsDataOptions): UseContributorsDataResult => {
	const { data, error } = useQueryInterop<
		useContributorsDataQuery$data,
		useContributorsDataQuery$variables
	>(
		graphql`
			query useContributorsDataQuery(
				$contentId: ID
				$contentStatus: [String]
				$contributorsMacroParameters: ConfluenceContributorsMacroParametersModel
			) {
				singleContent(id: $contentId, status: $contentStatus) {
					id
					contributorsMacro(contributorsMacroParameters: $contributorsMacroParameters) {
						authorRankingList {
							idString
							fullNameString
							edits
							comments
							labels
							watches
							lastEditTime
							lastCommentTime
							lastLabelTime
						}
					}
				}
			}
		`,
		{
			variables: {
				contentId,
				contentStatus: ['current'],
				contributorsMacroParameters: macroParams ?? undefined,
			},
			skip,
		},
	);

	return {
		data: data?.singleContent?.contributorsMacro ? data : undefined,
		loading: false,
		error: error ?? null,
	};
};
